/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useContext, useState } from "react";
import { Box, Flex, Link, LegacyText } from "flicket-ui";
import { FormikHelpers, FormikValues } from "formik";

import { Footer, Menu, Seo } from "~components";
import { BaseContext } from "~context";
import { useIsMobile, useAccount } from "~hooks";
import {
  ContentContainer,
  AuthContainer,
  MobileLogo,
  SignupForm,
} from "~components/auth";
import { Layout } from "~components/common/Layout";
import { useReferralCode } from "~hooks/useReferralCode";

const Signup = () => {
  const { organization } = useContext(BaseContext);
  const isMobile = useIsMobile();
  const { register } = useAccount();
  const [isAwaitingVerification, setAwaitingVerification] = useState(false);
  const { referralCode } = useReferralCode();

  const handleSubmit = async (
    {
      firstName,
      lastName,
      email,
      password,
      enableTwoFactorAuthentication,
    }: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    const user = await register({
      firstName,
      lastName,
      email,
      password,
      enableTwoFactorAuthentication,
      userAcquisition: { referralCodeId: referralCode },
    });
    if (!user) {
      actions.setSubmitting(false);
    } else if (!user?.hasVerifiedEmail) {
      setAwaitingVerification(true);
    }
  };

  return (
    <>
      <Seo
        title="Signup"
        description={`Create an account for ${organization?.name}`}
      />
      <Menu />
      <Layout layoutType="auth">
        <ContentContainer>
          <MobileLogo logo={organization?.branding?.logo} />
          <Flex
            bg="N100"
            justifyContent="flex-start"
            flex={1}
            alignItems="center"
            flexDir="column"
          >
            <AuthContainer>
              <Box w="100%">
                {isAwaitingVerification ? (
                  <>
                    <LegacyText
                      fontSize={6}
                      mb={3}
                      fontWeight="heavy"
                      color="N800"
                      textAlign="center"
                    >
                      Awaiting verification
                    </LegacyText>

                    <LegacyText variant="paragraph" mb={2}>
                      A login link has been sent to your email address to verify
                      your account.
                      <br />
                      If you can&apos;t find the email check your spam folder,
                      or request a new one from the login page.
                    </LegacyText>
                  </>
                ) : (
                  <>
                    <LegacyText
                      fontSize={6}
                      fontWeight="heavy"
                      color="N800"
                      textAlign="center"
                    >
                      Sign up
                    </LegacyText>
                    <SignupForm handleSubmit={handleSubmit} />
                  </>
                )}
              </Box>
            </AuthContainer>
            <Flex
              justifyContent="center"
              alignItems="center"
              mt={2}
              mb={{ _: 3, md: "28px" as any }}
            >
              <LegacyText
                fontSize={1}
                lineHeight="high"
                fontWeight="demiBold"
                mr="1/2"
              >
                Already have an account?
              </LegacyText>
              <Link
                to="/login"
                color="P300"
                textDecoration="underline"
                fontWeight="heavy"
                fontSize={1}
              >
                Log in
              </Link>
            </Flex>
          </Flex>
          {!isMobile && <Footer />}
        </ContentContainer>
      </Layout>
    </>
  );
};

export default Signup;
